import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../theme/getTheme'

export function swatchBgStyle(hex: string): { backgroundColor: string } {
  return { backgroundColor: hex }
}

export function getAccountBrandingTabStyles(theme: Theme) {
  return StyleSheet.create({
    scrollView: {
      flex: 1,
      ...(Platform.OS === 'web' && {
        minHeight: 0 as never,
      }),
    },
    scrollContent: {
      paddingTop: theme.spacing.md,
      paddingBottom: theme.spacing.xxl,
    },
    embeddedBody: {
      width: '100%',
      alignSelf: 'stretch',
    },
    section: {
      marginBottom: theme.spacing.xxl,
    },
    /** Section above a hairline — avoids double gap with `sectionDivider`. */
    sectionFlushBottom: {
      marginBottom: 0,
    },
    sectionDivider: {
      height: StyleSheet.hairlineWidth,
      width: '100%',
      alignSelf: 'stretch',
      backgroundColor: theme.gray[200],
      marginTop: theme.spacing.md,
      marginBottom: theme.spacing.xxl,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.gray[900],
      marginBottom: theme.spacing.md,
    },
    bodyText: {
      fontSize: 14,
      color: theme.gray[600],
      marginBottom: theme.spacing.lg,
    },
    /** Muted limits line under “Upload logos” (placeholder-style helper). */
    uploadLimitsPlaceholder: {
      fontSize: 13,
      color: theme.gray[500],
      marginBottom: theme.spacing.sm,
    },
    errorText: {
      fontSize: 13,
      color: theme.error,
      marginBottom: theme.spacing.md,
    },
    colorSlotRow: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.lg,
    },
    colorSlotTab: {
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.lg,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
    },
    colorSlotTabActive: {
      borderColor: theme.primary,
      backgroundColor: theme.background.cardElevated,
    },
    colorSlotTabText: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.gray[600],
    },
    colorSlotTabTextActive: {
      color: theme.gray[900],
    },
    row: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.md,
      marginBottom: theme.spacing.lg,
    },
    swatch: {
      width: 36,
      height: 36,
      borderRadius: 8,
      borderWidth: 2,
      borderColor: theme.gray[200],
    },
    swatchSelected: {
      borderColor: theme.primary,
      borderWidth: 3,
    },
    hexInput: {
      borderWidth: 1,
      borderColor: theme.gray[300],
      borderRadius: 8,
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      fontSize: 15,
      color: theme.gray[900],
      minWidth: 120,
      backgroundColor: theme.background.card,
    },
    primaryButton: {
      backgroundColor: theme.primary,
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      borderRadius: 10,
      alignSelf: 'flex-start',
      flexDirection: 'row',
      alignItems: 'center',
    },
    uploadLogoButton: {
      marginTop: theme.spacing.md,
    },
    saveColorsButton: {
      marginTop: theme.spacing.xxl,
    },
    primaryButtonPressed: {
      opacity: 0.85,
    },
    primaryButtonHover: {
      opacity: 0.92,
    },
    primaryButtonDisabled: {
      opacity: 0.5,
    },
    primaryButtonText: {
      color: theme.white,
      fontWeight: '700',
      fontSize: 15,
    },
    logoPreviewRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    logoThumbColumn: {
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    logoActionsRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.sm,
    },
    downloadLogoButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.primary,
      backgroundColor: theme.background.card,
    },
    downloadLogoButtonPressed: {
      opacity: 0.85,
    },
    downloadLogoButtonHover: {
      opacity: 0.92,
    },
    downloadLogoButtonDisabled: {
      opacity: 0.45,
    },
    downloadLogoButtonText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.primary,
    },
    logoThumb: {
      width: 64,
      height: 64,
      borderRadius: 8,
      backgroundColor: theme.gray[100],
    },
    removeLogoButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: 8,
      backgroundColor: theme.gray[200],
    },
    removeLogoButtonPressed: {
      opacity: 0.85,
    },
    removeLogoButtonHover: {
      opacity: 0.92,
    },
    removeLogoButtonDisabled: {
      opacity: 0.45,
    },
    removeLogoButtonText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.error,
    },
    /** Wrapper around each tile (Logo, Banner) in the Brand Imagery section. */
    brandImageryTileBlock: {
      marginBottom: theme.spacing.xl,
    },
    brandImageryTileLabel: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.gray[900],
      marginBottom: theme.spacing.xs,
    },
    /** Empty-state square placeholder for the Logo tile. */
    brandImageryEmptyTile: {
      width: 96,
      height: 96,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
    },
    /** Empty-state wide placeholder for the Banner tile (4:1). */
    brandImageryEmptyTileWide: {
      width: '100%',
      maxWidth: 480,
      aspectRatio: 4,
      borderRadius: 12,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.gray[300],
      backgroundColor: theme.background.card,
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
    },
    brandImageryEmptyTilePressed: {
      opacity: 0.85,
    },
    brandImageryEmptyTileHover: {
      opacity: 0.92,
      borderColor: theme.primary,
    },
    brandImageryEmptyTileDisabled: {
      opacity: 0.45,
    },
    brandImageryEmptyTileText: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.primary,
      textAlign: 'center',
    },
    /** Filled banner preview (4:1). */
    bannerPreviewColumn: {
      gap: theme.spacing.sm,
      marginBottom: theme.spacing.lg,
    },
    bannerThumb: {
      width: '100%',
      maxWidth: 480,
      aspectRatio: 4,
      borderRadius: 12,
      backgroundColor: theme.gray[100],
    },
    /** Toggle row for "use banner as squad background". */
    useBannerToggleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.lg,
      paddingTop: theme.spacing.md,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.gray[200],
      marginTop: theme.spacing.md,
    },
    useBannerToggleText: {
      flex: 1,
    },
    useBannerToggleLabel: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.gray[900],
      marginBottom: theme.spacing.xs,
    },
    useBannerToggleHint: {
      fontSize: 12,
      color: theme.gray[600],
    },
  })
}
